import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTreatyClient } from '../../../lib/client';
import type { Menu } from '../types';

export const useMenuActions = (refetch: () => void) => {
  const [loading, setLoading] = useState(false);
  const client = useTreatyClient();
  const navigate = useNavigate();

  const goToPairingDetail = useCallback(
    (menu: Menu) => {
      navigate('/pairing', { state: { menu } });
    },
    [navigate],
  );

  const handleStatusChange = useCallback(
    async (checked: boolean, record: Menu) => {
      try {
        setLoading(true);
        const response = await client.api
          .menu({
            id: record.id,
          })
          .put({
            name: record.name,
            description: record.description,
            price: record.price,
            pairing_id: record.pairing_id,
            tags: record.tags || [],
            status: checked ? 'enabled' : 'disabled',
          });

        if (response.data?.success) {
          message.success(`Status of ${record.name} updated successfully`);
          refetch();
        } else {
          message.error(response.data?.message || 'Failed to update status');
        }
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An error occurred while updating status');
        }
      } finally {
        setLoading(false);
      }
    },
    [client, refetch],
  );

  const handleDelete = useCallback(
    async (records: Menu[]) => {
      try {
        setLoading(true);
        const ids = records.map((record) => record.id);
        const response = await client.api.menu.delete({
          ids,
        });
        if (response.data?.success) {
          if (records.length === 1) {
            message.success(`${records[0].name} deleted successfully`);
          } else {
            message.success(`${records.length} dishes deleted successfully`);
          }
          refetch();
        } else {
          message.error(response.data?.message || 'Failed to delete dishes');
        }
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An error occurred while deleting dishes');
        }
      } finally {
        setLoading(false);
      }
    },
    [client, refetch],
  );

  const regeneratePairing = useCallback(
    async (menu: Menu) => {
      try {
        setLoading(true);
        const response = await client.api.menu
          .regenerate_pairing({ id: menu.id })
          .post();
        if (response.data?.success) {
          message.success('Pairing regenerated successfully');
          refetch();
        } else {
          message.error(
            response.data?.message || 'Failed to regenerate pairing',
          );
        }
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An error occurred while regenerating pairing');
        }
      } finally {
        setLoading(false);
      }
    },
    [client, refetch],
  );

  return {
    loading,
    handleStatusChange,
    handleDelete,
    goToPairingDetail,
    regeneratePairing,
  };
};
